import { SettingsPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import { t, useTranslation } from "@redotech/redo-i18n";
import AlertIcon from "@redotech/redo-icons/alert-triangle.svg";
import CreditCardIcon from "@redotech/redo-icons/credit-card-02.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  billingErrorMessage,
  updateStripe,
} from "@redotech/redo-merchant-general-app/client/stripe";
import { toast } from "@redotech/redo-web/alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoClientContext } from "@redotech/redo-web/client";
import { Flex } from "@redotech/redo-web/flex";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { SettingsRowLabel } from "@redotech/redo-web/settings-rows/settings-row-label";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { CollectPaymentMethod } from "./collect-payment-method";
import { PaymentMethodActionsMenu } from "./payment-method-actions-menu";

export function useLocalizedBillingStatus({ enabled = true } = {}) {
  return useRpcQuery(
    RedoMerchantRpcClientContext,
    "getLocalizedBillingStatus",
    {},
    { enabled },
  );
}

function cardTitle({
  brand,
  last4,
}: {
  brand: string | null;
  last4: string | null;
}): string {
  return t("{{paymentMethod}} ending in {{last4}}", {
    paymentMethod: brand
      ? brand.charAt(0).toUpperCase() + brand.slice(1)
      : t("Card"),
    last4: last4 ?? "",
  });
}

export const LocalizedBilling = memo(function LocalizedBilling() {
  const { t } = useTranslation();
  const team = useRequiredContext(TeamContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const redoClient = useRequiredContext(RedoClientContext);
  const canEditSettings = useHasPermission(
    SettingsPermissions.SET_BILLING_SETTINGS,
  );
  const { data: status, refetch } = useLocalizedBillingStatus();
  const [collecting, setCollecting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [deleteLoad, doDelete] = useTriggerLoad(async () => {
    if (!status?.available) {
      return;
    }
    try {
      await rpcClient.deletePaymentMethod({
        primary: true,
        currency: status.currency,
      });
      setDeleteModalOpen(false);
      await refetch();
    } catch (error) {
      console.error(error);
      toast(t("Unable to delete payment method"), { variant: "error" });
    }
  });

  if (!status?.available) {
    return null;
  }
  const { currency, publishableKey, paymentMethod } = status;

  const onConfirmed = async (
    paymentMethodID: string,
    setupIntentClientSecret: string | null,
  ) => {
    try {
      await updateStripe(redoClient, {
        paymentMethodID,
        primary: true,
        setupIntentClientSecret,
        currency,
      });
      setCollecting(false);
      toast(t("Payment method saved."), { variant: "success" });
      await refetch();
    } catch (error) {
      console.error(error);
      toast(
        billingErrorMessage(error) ??
          t(
            "We couldn't save your payment method. Please try again, or contact support if it keeps happening.",
          ),
        { variant: "error" },
      );
    }
  };

  const title = t("{{currency}} billing", { currency });

  return (
    <Flex dir="column" gap="md" w="full">
      {collecting ? (
        <Flex dir="column" gap="lg" w="full">
          <SettingsRowLabel title={title} />
          <CollectPaymentMethod
            localizedAccount={{ currency, publishableKey }}
            onConfirmed={onConfirmed}
            submitLabel={t("Save card")}
            team={team}
          />
          <Flex justify="flex-end" w="full">
            <RedoButton
              hierarchy="secondary"
              onClick={() => setCollecting(false)}
              size="sm"
              text={t("Discard")}
            />
          </Flex>
        </Flex>
      ) : (
        <Flex align="center" gap="lg" w="full">
          <RedoFeaturedIcon
            color="gray"
            Icon={CreditCardIcon}
            shape="circle"
            size="md"
          />
          <Flex dir="column" gap="none" grow={1} minW="none">
            <Text fontSize="sm" fontWeight="medium" textColor="primary">
              {paymentMethod ? cardTitle(paymentMethod) : title}
            </Text>
            <Text fontSize="sm" textColor="tertiary">
              {paymentMethod
                ? t("Billed in {{currency}}", { currency })
                : t(
                    "Add a card to be billed in {{currency}} by Redo's local entity. Your payment methods above stay on file for USD charges.",
                    { currency },
                  )}
            </Text>
          </Flex>
          <Flex shrink={0}>
            <RedoBadge color="gray" size="sm" text={currency} />
          </Flex>
          <Flex shrink={0}>
            {paymentMethod ? (
              <PaymentMethodActionsMenu
                disabled={!canEditSettings}
                items={[
                  {
                    text: t("Replace"),
                    onClick: () => setCollecting(true),
                    disabled: deleteLoad.pending,
                  },
                  {
                    text: t("Delete"),
                    onClick: () => setDeleteModalOpen(true),
                    theme: "destructive",
                    disabled: deleteLoad.pending,
                  },
                ]}
                pending={deleteLoad.pending}
              />
            ) : (
              <RedoButton
                disabled={!canEditSettings}
                hierarchy="secondary"
                onClick={() => setCollecting(true)}
                size="sm"
                text={t("Add")}
              />
            )}
          </Flex>
        </Flex>
      )}
      <RedoModal
        isOpen={deleteModalOpen}
        onModalCloseRequested={() => {
          if (!deleteLoad.pending) {
            setDeleteModalOpen(false);
          }
        }}
        primaryButton={{
          text: t("Delete"),
          onClick: () => doDelete(),
          theme: "destructive",
          pending: deleteLoad.pending,
          size: "sm",
        }}
        secondaryButton={{
          text: t("Cancel"),
          onClick: () => setDeleteModalOpen(false),
          disabled: deleteLoad.pending,
          size: "sm",
        }}
        theme="warn"
        title={t("Delete {{currency}} payment method?", { currency })}
        TitleIcon={AlertIcon}
      >
        <Text fontSize="sm" textColor="secondary">
          {t(
            "{{currency}} bills will go back to your payment methods above until you add a new card.",
            { currency },
          )}
        </Text>
      </RedoModal>
    </Flex>
  );
});
